import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { tradePath, myVaultPath, autoInvestPath } from 'components/Routes'
import useSpotPrice from 'hooks/useSpotPrice'
import { useProductPerformance } from 'hooks/useInventory'
import { useMutateCreateReviewOrder } from 'hooks/useTrading'
import { memo, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formattedPrice } from 'utils/util'
import { formatWeightAsGrams } from 'utils/formatWeightAsGrams'
import { voucherFlowPath } from 'components/Routes/Routes'
import { ProductInclLocal } from 'hooks/useProducts'
const drawerBleeding = 80

interface TradeSubNavMenuProps {
  product?: ProductInclLocal
  onClose: () => void
  vaultId?: number
}

function TradeSubNavMenu({
  product,
  onClose,
  vaultId
}: Readonly<TradeSubNavMenuProps>): JSX.Element {
  const { setToast } = useAuth()
  const productId = product?.id

  const navigate = useNavigate()

  const [, { data: spotPriceRaw }] = useSpotPrice({
    productId: productId ? Number(productId) : 0
  })
  const latestSpotPrice = spotPriceRaw

  const { data: productPerformanceData } = useProductPerformance(
    productId ? Number(productId) : 0,
    vaultId
  )

  const { mutateAsync: createReviewOrder } = useMutateCreateReviewOrder()

  const getMidpointPerUnit = useCallback(() => {
    const midpointPerMg = latestSpotPrice?.midpointPerMg
    return midpointPerMg ? Number(midpointPerMg).toFixed(4) : '0.0000'
  }, [latestSpotPrice])

  const getCurrentValue = useCallback(() => {
    const currentValue = productPerformanceData?.currentValue

    const amount = Number(currentValue).toFixed(2)
    return amount ? formattedPrice(amount) : '0.00'
  }, [productPerformanceData])

  const currentValueAmount = useMemo(() => {
    return Number(productPerformanceData?.currentValue ?? 0)
  }, [productPerformanceData])

  const getQuantityOwned = useCallback(() => {
    const quantityOwned = productPerformanceData?.quantityOwned

    return quantityOwned ? Number(quantityOwned) : 0
  }, [productPerformanceData])

  const handleClickBuyMore = useCallback(() => {
    navigate(tradePath({ routeParams: { productId } }))
  }, [navigate, productId])

  const handleClickSell = useCallback(async () => {
    if (currentValueAmount <= 0) {
      return
    }

    try {
      const reviewOrder = await createReviewOrder({
        productId: Number(productId),
        orderType: 'sell',
        quantityMg: 100
      })
      const { id } = reviewOrder ?? {}
      if (id) {
        navigate(
          myVaultPath({
            queryParams: { type: 'sell', id, productId }
          })
        )
        onClose()
      }
    } catch (error) {
      setToast({
        open: true,
        message: error instanceof Error ? error.message : 'An error occurred',
        type: 'error',
        duration: 3000
      })
    }
  }, [
    currentValueAmount,
    navigate,
    productId,
    createReviewOrder,
    onClose,
    setToast
  ])

  return (
    <Box>
      <Box textAlign='right'>
        <IconButton aria-label='Close drawer' edge='start' onClick={onClose}>
          <CloseIcon fontSize='large' />
        </IconButton>
      </Box>
      <Stack height={drawerBleeding} id='bullion'>
        <Box paddingX={2} display='flex' justifyContent='space-between'>
          <Box>
            <Typography color='primary' fontSize='1rem'>
              {product?.name.toUpperCase()} BULLION
            </Typography>
            <Typography color='primary' fontSize='1rem'>
              99.99% FINE {product?.name.toUpperCase()}
            </Typography>
          </Box>
          <Box textAlign='right'>
            <Typography color='primary' fontSize='0.75rem'>
              PER GRAM: ${(Number(getMidpointPerUnit()) * 1000).toFixed(2)}
            </Typography>
            <Typography color='primary' fontSize='0.75rem'>
              YOU OWN: ${getCurrentValue()}
            </Typography>
          </Box>
        </Box>
      </Stack>

      <Stack px={2} pb={4}>
        <Typography color='common.black' fontSize='1rem'>
          Current value is ${getCurrentValue()}.
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          You own {formatWeightAsGrams(getQuantityOwned())}.
        </Typography>
      </Stack>

      <Stack px={2} py={3} gap={1}>
        {/* Held platinum with the flag off is sell-only. */}
        {product?.buyable !== false && (
          <>
            <Button
              variant='outlined'
              fullWidth
              onClick={() => navigate(autoInvestPath())}
            >
              Auto Invest
            </Button>
            <Button variant='outlined' fullWidth onClick={handleClickBuyMore}>
              Buy More
            </Button>
          </>
        )}
        <Button
          variant='outlined'
          fullWidth
          onClick={() => navigate(voucherFlowPath())}
        >
          Gift
        </Button>
        {currentValueAmount > 0 && (
          <Button variant='outlined' fullWidth onClick={handleClickSell}>
            Sell
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default memo(TradeSubNavMenu)
